import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import BlogPost from '../models/BlogPost';
import './ManagePosts.css';

interface ManagePostsProps {
  onPageChange: (page: string) => void;
}

const ManagePosts: React.FC<ManagePostsProps> = ({ onPageChange }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Form state for creating new posts
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: '',
    tags: '',
    imageUrl: '',
    imageAltText: '',
    isPublished: false,
    isFeatured: false,
    readTimeMinutes: 5,
    metaDescription: '',
    externalLinks: ''
  });

  useEffect(() => {
    loadPosts();
  }, [currentPage]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedPosts = await ApiService.getBlogPosts(currentPage, pageSize);
      setPosts(fetchedPosts);
    } catch (err) {
      setError('Failed to load blog posts. Please try again.');
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const blogPostData = {
        title: newPost.title,
        excerpt: newPost.excerpt,
        content: newPost.content,
        author: newPost.author,
        category: newPost.category,
        tags: newPost.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        imageUrl: newPost.imageUrl,
        imageAltText: newPost.imageAltText,
        isPublished: newPost.isPublished,
        isFeatured: newPost.isFeatured,
        readTimeMinutes: newPost.readTimeMinutes,
        metaDescription: newPost.metaDescription,
        externalLinks: newPost.externalLinks.split(',').map(link => link.trim()).filter(link => link)
      };

      const result = await ApiService.createBlogPost(blogPostData);
      
      if (result.success) {
        // Reset form
        setNewPost({
          title: '',
          excerpt: '',
          content: '',
          author: '',
          category: '',
          tags: '',
          imageUrl: '',
          imageAltText: '',
          isPublished: false,
          isFeatured: false,
          readTimeMinutes: 5,
          metaDescription: '',
          externalLinks: ''
        });
        setShowCreateForm(false);
        
        // Reload posts to show the new one
        await loadPosts();
        
        alert('Blog post created successfully!');
      } else {
        alert(`Failed to create blog post: ${result.message}`);
      }
    } catch (err) {
      console.error('Error creating post:', err);
      alert('Error creating blog post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setNewPost({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      category: post.category,
      tags: post.tags?.join(', ') || '',
      imageUrl: post.imageUrl || '',
      imageAltText: post.imageAltText || '',
      isPublished: post.isPublished,
      isFeatured: post.isFeatured,
      readTimeMinutes: post.readTimeMinutes || 5,
      metaDescription: post.metaDescription || '',
      externalLinks: post.externalLinks?.join(', ') || ''
    });
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  const handleUpdatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingPost) return;
    
    try {
      setLoading(true);
      
      const blogPostData = {
        title: newPost.title,
        excerpt: newPost.excerpt,
        content: newPost.content,
        author: newPost.author,
        category: newPost.category,
        tags: newPost.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        imageUrl: newPost.imageUrl,
        imageAltText: newPost.imageAltText,
        isPublished: newPost.isPublished,
        isFeatured: newPost.isFeatured,
        readTimeMinutes: newPost.readTimeMinutes,
        metaDescription: newPost.metaDescription,
        externalLinks: newPost.externalLinks.split(',').map(link => link.trim()).filter(link => link)
      };

      const result = await ApiService.updateBlogPost(editingPost.id, blogPostData);
      
      if (result.success) {
        // Reset form
        setNewPost({
          title: '',
          excerpt: '',
          content: '',
          author: '',
          category: '',
          tags: '',
          imageUrl: '',
          imageAltText: '',
          isPublished: false,
          isFeatured: false,
          readTimeMinutes: 5,
          metaDescription: '',
          externalLinks: ''
        });
        setShowEditForm(false);
        setEditingPost(null);
        
        // Reload posts to show the updated one
        await loadPosts();
        
        alert('Blog post updated successfully!');
      } else {
        alert(`Failed to update blog post: ${result.message}`);
      }
    } catch (err) {
      console.error('Error updating post:', err);
      alert('Error updating blog post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (post: BlogPost) => {
    if (!confirm(`Are you sure you want to delete "${post.title}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      const result = await ApiService.deleteBlogPost(post.id);
      
      if (result.success) {
        // Reload posts to remove the deleted one
        await loadPosts();
        alert('Blog post deleted successfully!');
      } else {
        alert(`Failed to delete blog post: ${result.message}`);
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Error deleting blog post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingPost(null);
    setNewPost({
      title: '',
      excerpt: '',
      content: '',
      author: '',
      category: '',
      tags: '',
      imageUrl: '',
      imageAltText: '',
      isPublished: false,
      isFeatured: false,
      readTimeMinutes: 5,
      metaDescription: '',
      externalLinks: ''
    });
  };

  const filteredPosts = posts.filter(post =>
    post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.author?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectPost = (postId: string) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPosts.size === filteredPosts.length) {
      setSelectedPosts(new Set());
    } else {
      setSelectedPosts(new Set(filteredPosts.map(post => post.id)));
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && posts.length === 0) {
    return (
      <div className="manage-posts">
        <div className="loading">Loading blog posts...</div>
      </div>
    );
  }

  return (
    <div className="manage-posts">
      <div className="manage-posts-header">
        <h2>Manage Blog Posts</h2>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => {
              if (showCreateForm || showEditForm) {
                setShowCreateForm(false);
                setShowEditForm(false);
                setEditingPost(null);
              } else {
                setShowCreateForm(true);
              }
            }}
          >
            {(showCreateForm || showEditForm) ? 'Cancel' : 'Create New Post'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadPosts} className="btn btn-link">Retry</button>
        </div>
      )}

      {(showCreateForm || showEditForm) && (
        <div className="create-post-form">
          <h3>{showEditForm ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
          <form onSubmit={showEditForm ? handleUpdatePost : handleCreatePost}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="author">Author *</label>
                <input
                  type="text"
                  id="author"
                  value={newPost.author}
                  onChange={(e) => setNewPost({ ...newPost, author: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="excerpt">Excerpt *</label>
              <textarea
                id="excerpt"
                value={newPost.excerpt}
                onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="content">Content *</label>
              <textarea
                id="content"
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                rows={10}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <input
                  type="text"
                  id="category"
                  value={newPost.category}
                  onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="tags">Tags (comma-separated)</label>
                <input
                  type="text"
                  id="tags"
                  value={newPost.tags}
                  onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="imageUrl">Image URL</label>
                <input
                  type="url"
                  id="imageUrl"
                  value={newPost.imageUrl}
                  onChange={(e) => setNewPost({ ...newPost, imageUrl: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="imageAltText">Image Alt Text</label>
                <input
                  type="text"
                  id="imageAltText"
                  value={newPost.imageAltText}
                  onChange={(e) => setNewPost({ ...newPost, imageAltText: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="readTimeMinutes">Read Time (minutes)</label>
                <input
                  type="number"
                  id="readTimeMinutes"
                  value={newPost.readTimeMinutes}
                  onChange={(e) => setNewPost({ ...newPost, readTimeMinutes: parseInt(e.target.value) || 5 })}
                  min="1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="metaDescription">Meta Description</label>
                <input
                  type="text"
                  id="metaDescription"
                  value={newPost.metaDescription}
                  onChange={(e) => setNewPost({ ...newPost, metaDescription: e.target.value })}
                  placeholder="SEO description"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="externalLinks">External Links (comma-separated URLs)</label>
              <input
                type="text"
                id="externalLinks"
                value={newPost.externalLinks}
                onChange={(e) => setNewPost({ ...newPost, externalLinks: e.target.value })}
                placeholder="https://example.com, https://another.com"
              />
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPost.isPublished}
                    onChange={(e) => setNewPost({ ...newPost, isPublished: e.target.checked })}
                  />
                  Published
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPost.isFeatured}
                    onChange={(e) => setNewPost({ ...newPost, isFeatured: e.target.checked })}
                  />
                  Featured
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (showEditForm ? 'Updating...' : 'Creating...') : (showEditForm ? 'Update Post' : 'Create Post')}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={showEditForm ? handleCancelEdit : () => setShowCreateForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="posts-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {filteredPosts.length > 0 && (
          <div className="bulk-actions">
            <label>
              <input
                type="checkbox"
                checked={selectedPosts.size === filteredPosts.length && filteredPosts.length > 0}
                onChange={handleSelectAll}
              />
              Select All ({selectedPosts.size} selected)
            </label>
            {selectedPosts.size > 0 && (
              <div className="bulk-buttons">
                <button className="btn btn-warning" disabled title="Bulk edit functionality coming soon">
                  Bulk Edit (Coming Soon)
                </button>
                <button className="btn btn-danger" disabled title="Bulk delete functionality coming soon">
                  Bulk Delete (Coming Soon)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="posts-table">
        {filteredPosts.length === 0 ? (
          <div className="no-posts">
            {searchTerm ? 'No posts found matching your search.' : 'No blog posts available.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Select</th>
                <th>Title</th>
                <th>Author</th>
                <th>Category</th>
                <th>Status</th>
                <th>Published</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map((post) => (
                <tr key={post.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedPosts.has(post.id)}
                      onChange={() => handleSelectPost(post.id)}
                    />
                  </td>
                  <td>
                    <div className="post-title">
                      {post.title}
                      {post.isFeatured && <span className="featured-badge">Featured</span>}
                    </div>
                  </td>
                  <td>{post.author}</td>
                  <td>{post.category}</td>
                  <td>
                    <span className={`status-badge ${post.isPublished ? 'published' : 'draft'}`}>
                      {post.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td>{formatDate(post.publishedAt)}</td>
                  <td>{post.viewCount || 0}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          // Navigate to view post - you can implement this later
                          console.log('View post:', post.id);
                        }}
                      >
                        View
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditPost(post)}
                        disabled={loading}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeletePost(post)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredPosts.length > 0 && (
        <div className="pagination">
          <button 
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">Page {currentPage}</span>
          <button 
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={filteredPosts.length < pageSize}
          >
            Next
          </button>
        </div>
      )}

      <div className="manage-posts-footer">
        <button 
          className="btn btn-link"
          onClick={() => onPageChange('home')}
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default ManagePosts;